import * as React from "react";
import { Steps, Input, Button, Form } from "antd";
import { sitemapStore } from "Pages/Project/Overview/Stores/OverviewSitemapStore";
import { observer } from "mobx-react";
import { EmailTypeStore } from "../Stores/EmailTypeStore";
// import { EmailTypeStore } from '../Stores/EmailTypeStore';
// import { createFileName } from "../utils/createFileName";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { FolderPicker } from "../../../../Components/FolderPicker";
import { EmailSearchStore } from "../Stores/EmailSearchStore";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";

const FormItem = Form.Item;

const SpecialInput = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { value, onChange, projectSpecific = false }: { value?: any; onChange?: Function; projectSpecific: boolean }
) => {
  const [inputValue, setInputValue] = React.useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange({ name: e.target.value, projectSpecific });
  };
  React.useEffect(() => {
    if (value && value.name !== inputValue) {
      setInputValue(value.name);
    }
  }, []);
  return <Input value={inputValue} onChange={handleChange} addonAfter={".pdf"} />;
};

interface FilesFormProps extends WithTranslation {
  fileList: { id: string; name: string; title: string; projectSpecific: boolean }[];
  setFileList: (list: MoveToProjectModalState["selectedEmails"]) => void;
  prev: () => void;
}

export const FilesForm = withTranslation(I18N_NS.PROJECT)((props: FilesFormProps) => {
  const { fileList, setFileList, prev, t } = props;
  const [form] = Form.useForm();
  const handleFinish = (values) => {
    setFileList(values);
  };

  const initialValues = fileList.reduce((obj, curr) => {
    obj[curr.id] = {
      // eslint-disable-next-line no-useless-escape
      name: curr.name && curr.name.replace(/[\<\>\:\"\/\\\|\?\|\*]/gi, ""),
      projectSpecific: curr.projectSpecific,
    };
    return obj;
  }, {});

  return (
    <Form layout="vertical" onFinish={handleFinish} form={form} requiredMark={false} initialValues={initialValues}>
      {fileList.map((file) => (
        <FormItem
          key={file.id}
          name={file.id}
          label={<span dangerouslySetInnerHTML={{ __html: t(`Save email "{{name}}" as`, { name: file.title }) }} />}
          rules={[{ required: true, message: t("This field is required") }]}
        >
          <SpecialInput projectSpecific={file.projectSpecific} />
        </FormItem>
      ))}
      <Button type="primary" htmlType="submit">
        {t("Next")}
      </Button>
      <Button onClick={prev} style={{ marginLeft: 8 }}>
        {t("Back")}
      </Button>
    </Form>
  );
});

const FolderTree = observer(({ handleSelectFolder, selectedFolderId }) => {
  return <FolderPicker onSelectFolder={handleSelectFolder} selectedFolderId={selectedFolderId} minAccessLevel="VIEW" />;
});

interface MoveToProjectModalProps extends WithTranslation, ModalComponentProps {
  selectedItems: API_MinimumMailInfo[];
  emailStore: EmailTypeStore | EmailSearchStore;
  goBack: () => void;
}

interface MoveToProjectModalState {
  current: number;
  selectedFolderId: number;
  selectedFolderName: string;
  selectedEmails: { [id: string]: { name: string; projectSpecific: boolean } };
  loading: boolean;
  /* Map of { EmailId: Filename } */
  defaultFilenames: { [key: string]: string };
}

export const MoveToProjectModal = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<MoveToProjectModalProps, MoveToProjectModalState> {
      constructor(props: MoveToProjectModalProps) {
        super(props);

        this.state = {
          current: 0,
          selectedFolderId: undefined,
          selectedFolderName: "",
          selectedEmails: {},
          loading: false,
          defaultFilenames: {},
        };
      }

      componentDidMount(): void {
        this.initializeFilenames();
      }

      componentWillUnmount() {
        sitemapStore.clear();
      }

      handleSelectFolder = ({ id, name }) => {
        this.setState({ selectedFolderId: id, selectedFolderName: name });
      };

      next = () => {
        const current = this.state.current + 1;
        this.setState({ current });
      };

      prev = () => {
        const current = this.state.current - 1;
        this.setState({ current });
      };

      setSelectedEmails = (selectedEmails: MoveToProjectModalState["selectedEmails"]) => {
        this.setState({ selectedEmails, current: this.state.current + 1 });
      };

      initializeFilenames = async () => {
        try {
          const defaultFilenamesList = await this.props.emailStore.getDefaultFilenames(
            this.props.selectedItems.map((i) => i.id)
          );
          const defaultFilenames = defaultFilenamesList.reduce<{ [key: string]: string }>((buffer, item) => {
            buffer[item.id] = item.filename;
            return buffer;
          }, {});
          this.setState({ defaultFilenames });
        } catch (error) {
          throw error;
        }
      };

      submit = async () => {
        try {
          const { selectedEmails, selectedFolderId } = this.state;
          await this.props.emailStore.moveMailToProject(selectedEmails, selectedFolderId);
          this.props.goBack();
          this.props.hideModal();
        } catch (error) {
          if (error.response.data && error.response.data.code && error.response.data.code !== 1) {
            const { t } = this.props;
            const code = error.response.data.code;
            const title = t("Can not be moved to project");
            let message = "";
            if (code === 1057) {
              message = t("Empty names are not allowed.");
            } else if (code === 1058) {
              message = t("Duplicated names are not allowed.");
            } else if (code === 1059) {
              message = t("Filename already exists");
            }
            notificationError(title, message);
          }
          throw error;
        }
      };

      get fileList() {
        const { selectedItems } = this.props;
        const list = selectedItems.map((email) => {
          return {
            id: email.id,
            title: email.subject,
            name: this.state.defaultFilenames[email.id] || "",
            projectSpecific: email.projectSpecific,
          };
        });
        return list;
      }

      get steps() {
        const { selectedEmails, selectedFolderId, selectedFolderName } = this.state;
        const { t } = this.props;

        return [
          {
            title: t("Select folder"),
            content: <FolderTree handleSelectFolder={this.handleSelectFolder} selectedFolderId={selectedFolderId} />,
          },
          {
            title: t("Set filename(s)"),
            content: <FilesForm setFileList={this.setSelectedEmails} fileList={this.fileList} prev={this.prev} />,
          },
          {
            title: t("Confirm"),
            content: (
              <div>
                <p>
                  <b>{t("Foldername")}:</b> {selectedFolderName}
                </p>
                <p>
                  <b>{t("Filename(s)")}: </b>
                  {Object.keys(selectedEmails)
                    .map((key) => `${selectedEmails[key]?.name}.pdf`)
                    .join(", ")}
                </p>
              </div>
            ),
          },
        ];
      }

      render() {
        const { current, selectedFolderId } = this.state;
        const { emailStore, t } = this.props;

        return (
          <>
            <Steps
              current={current}
              size="small"
              progressDot={true}
              items={this.steps.map((item) => ({ key: item.title, title: item.title }))}
            />
            <div style={{ marginTop: 20 }}>{this.steps[this.state.current].content}</div>
            <div style={{ marginTop: 20 }}>
              {this.state.current === this.steps.length - 1 ? (
                <>
                  <Button type="primary" loading={emailStore.actionState === "LOADING"} onClick={this.submit}>
                    {t("Done")}
                  </Button>
                  <Button style={{ marginLeft: 8 }} onClick={this.prev}>
                    {t("Back")}
                  </Button>
                </>
              ) : this.state.current > 0 ? null : (
                // eslint-disable-next-line max-len
                // </> //   <Button style={{ marginLeft: 8 }} onClick={this.prev}>Back</Button> //   <Button type="primary" disabled={!selectedFolderId} onClick={this.next}>Next</Button> // <> // eslint-disable-next-line max-len
                <Button type="primary" disabled={!selectedFolderId} onClick={this.next}>
                  {t("Next")}
                </Button>
              )}
            </div>
          </>
        );
      }
    }
  )
);
